import { ApplicationConfig, inject, provideAppInitializer, provideZonelessChangeDetection } from '@angular/core';
import { appRoutes } from './app.routes';
import { BrowserCacheLocation, InteractionType, IPublicClientApplication, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { environment } from '../environments/environment';
import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { MessageService } from 'primeng/api';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { MyPreset } from '@web/csl-lib-themes';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { providePrimeNG } from 'primeng/config';
import { provideRouter, withComponentInputBinding, withRouterConfig } from '@angular/router';
import { FEATURE_FLAG_ENVIRONMENT, FeatureFlagService, SERVICE_CONFIG_TOKEN } from '@web/csl-lib-services';
import { TokenBlocklistInterceptor } from '@web/csl-lib-interceptors';

export function loggerCallback(logLevel: LogLevel, message: string) {
  console.log(message);
}

export function MSALInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: environment.msalConfig.auth.clientId,
      authority: environment.b2cPolicies.authorities.signUpSignIn.authority,
      knownAuthorities: [environment.b2cPolicies.authorityDomain], // Mark your B2C tenant's domain as trusted.
      redirectUri: environment.msalConfig.redirectUri,
      postLogoutRedirectUri: environment.logoutRedirectUrl,
    },
    cache: {
      cacheLocation: BrowserCacheLocation.SessionStorage,
    },
    system: {
      allowPlatformBroker: false, // Disables WAM Broker
      loggerOptions: {
        loggerCallback,
        logLevel: environment.production ? LogLevel.Error : LogLevel.Info,
        piiLoggingEnabled: false,
      },
    },
  });
}


export function MSALInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const protectedResourceMap = new Map<string, Array<string>>();
  protectedResourceMap.set(
    `${environment.apiConfig.uri}/*`,
    environment.apiConfig.scopes
  );

  /*
    More protected resources can be added here. environment.apiConfig will also need to be modified to support multiple endpoints/resources.
    https://learn.microsoft.com/en-us/azure/active-directory-b2c/enable-authentication-angular-spa-app, see "MSAL interceptor configuration."

    protectedResourceMap.set(
     environment.apiConfig.uri + '/user-profiles',
      environment.apiConfig.scopes
    );
  */

  return {
    interactionType: InteractionType.Redirect,
    protectedResourceMap,
  };
}

export function MSALGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.apiConfig.scopes],
    },
    loginFailedRoute: '/login-failed',
  };
}


export const appConfig: ApplicationConfig = {
  providers: [
    provideZonelessChangeDetection(),
    provideRouter(appRoutes, withComponentInputBinding(), withRouterConfig({ paramsInheritanceStrategy: 'always' })),
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    {
      provide: HTTP_INTERCEPTORS,
      useClass: MsalInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: TokenBlocklistInterceptor, // Our interceptor handles blocklist detection (runs second)
      multi: true,
    },
    {
      provide: MSAL_INSTANCE,
      useFactory: MSALInstanceFactory,
    },
    {
      provide: MSAL_GUARD_CONFIG,
      useFactory: MSALGuardConfigFactory,
    },
    {
      provide: MSAL_INTERCEPTOR_CONFIG,
      useFactory: MSALInterceptorConfigFactory,
    },
    { provide: FEATURE_FLAG_ENVIRONMENT, useValue: environment.featureFlagEnvironment },
    // Feature flags must be resolved before the first render
    provideAppInitializer(() => inject(FeatureFlagService).load()),
    MessageService,
    MsalService,
    MsalGuard,
    MsalBroadcastService,
    provideAnimationsAsync(),
    providePrimeNG({
      theme: {
        preset: MyPreset,
        options: {
          darkModeSelector: '.fake-dark-mode',
        }
      },
      license: environment.primeNgLicenseKey,
    }),
    {
      provide: SERVICE_CONFIG_TOKEN, useValue: {
        apiUrl: environment.apiUrl,
        logoutRedirectUrl: environment.logoutRedirectUrl
      }
    },
  ],
};
